/*
 * Cart Quantity Gifts (blocks/cart-quantity-gifts.liquid) — keeps the cart's gift lines honest.
 *
 * The block's Gift children name each gift (data-cqg-gift: variant id + unlock quantity) and its config names
 * the counted products and the gift discount's title. After every cart change (PUB_SUB_EVENTS.cartUpdate,
 * cart:update) this script reads the cart the event carries and settles it:
 *   - an earned gift sits in the cart as exactly one unit (added with Sugar's gift line properties, so it is
 *     never counted and shows no quantity selector);
 *   - an unearned gift is taken out, whatever properties it was added with (a shopper adding it from its own
 *     page or by URL gets nothing);
 *   - a line the gift discount made free that no Gift block names is taken out.
 * One /cart/update.js and one /cart/add.js at most per settle, then one announcement so the drawer repaints.
 * A settled cart makes no request, so the announcement can't loop.
 *
 * Checkout: a click on a checkout button while the cart is out of step settles it first, then continues to
 * /checkout, so a gift can't ride along on a stale cart.
 *
 * On a page where Sugar's Gift Unlocks sells the same gift variants (a product page with offer gifts), the
 * lines Sugar added (_gift_unlock) are Sugar's to settle; this script still removes the others.
 */
(function () {
  'use strict';
  if (window.CartQuantityGifts) return;

  const ROOT = '[data-cart-quantity-gifts]';
  const AUTO_PROPS = ['_gift_unlock', '_gift_list', '_gift_picker', '_offer_gift', '_sugar_progress_goal', '_cart_toggle', '_cart_upsell'];
  const CHECKOUT = 'button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"], a[href$="/checkout"]';
  let busy = null;
  let dirty = false;
  let timer = null;
  let pending = null;
  let lastCart = null;
  const recent = [];

  function isCart(value) {
    return !!(value && Array.isArray(value.items) && typeof value.item_count === 'number');
  }

  function cartOf(detail) {
    if (isCart(detail)) return detail;
    if (detail && isCart(detail.cart)) return detail.cart;
    if (detail && isCart(detail.cartData)) return detail.cartData;
    return null;
  }

  function has(props, key) {
    const v = props && props[key];
    return v !== undefined && v !== null && v !== '' && v !== false;
  }

  function isAuto(item) {
    return AUTO_PROPS.some((key) => has(item.properties, key));
  }

  function config() {
    const root = document.querySelector(ROOT);
    if (!root) return null;
    let raw = {};
    try {
      const el = root.querySelector('[data-cqg-config]');
      raw = JSON.parse((el && el.textContent) || '{}');
    } catch (e) {
      return null;
    }
    const gifts = [];
    root.querySelectorAll('[data-cqg-gift]').forEach((el) => {
      const id = el.getAttribute('data-variant-id');
      const qty = parseInt(el.getAttribute('data-qty'), 10);
      if (id && qty > 0) gifts.push({ id: String(id), qty });
    });
    return {
      root,
      counted: new Set((raw.counted || []).map(String)),
      title: (raw.giftDiscountTitle || '').trim(),
      gifts,
    };
  }

  function freeByGiftDiscount(item, title) {
    if (!title) return false;
    return (item.line_level_discount_allocations || []).some(
      (a) => a && a.discount_application && a.discount_application.title === title
    );
  }

  /* Sugar's Gift Unlocks on this page already sells one of these gifts */
  function sugarOwns(cfg) {
    return cfg.gifts.some((gift) =>
      Array.prototype.some.call(
        document.querySelectorAll('[data-gift-unlocks] [data-gift-unlock][data-variant-id="' + gift.id + '"]'),
        (el) => !el.closest('sugar-cart-drawer')
      )
    );
  }

  /* What the cart needs to be honest: { updates: {key: qty}, additions: [...] } */
  function plan(cart, cfg) {
    const updates = {};
    const additions = [];
    const giftIds = new Set(cfg.gifts.map((g) => g.id));

    let units = 0;
    let productId = '';
    cart.items.forEach((item) => {
      if (isAuto(item) || giftIds.has(String(item.variant_id)) || freeByGiftDiscount(item, cfg.title)) return;
      if (!cfg.counted.has(String(item.product_id))) return;
      units += parseInt(item.quantity, 10) || 0;
      if (!productId) productId = String(item.product_id);
    });

    const sugar = sugarOwns(cfg);
    const seen = new Set();
    cfg.gifts.forEach((gift) => {
      if (seen.has(gift.id)) return;
      seen.add(gift.id);
      const earned = units > 0 && units >= Math.min.apply(null, cfg.gifts.filter((g) => g.id === gift.id).map((g) => g.qty));
      const lines = cart.items.filter((item) => String(item.variant_id) === gift.id);

      if (sugar) {
        if (!earned) lines.filter((l) => !has(l.properties, '_gift_unlock')).forEach((l) => { updates[l.key] = 0; });
        return;
      }
      if (!earned) {
        lines.forEach((l) => { updates[l.key] = 0; });
        return;
      }
      if (!lines.length) {
        additions.push({
          id: Number(gift.id),
          quantity: 1,
          properties: { _gift_unlock: 'true', _gift_for_product: productId, _gift_unlock_required_qty: String(gift.qty) },
        });
        return;
      }
      /* keep one unit, on the line a gift block added when there is one */
      lines.sort((a, b) => (has(b.properties, '_gift_unlock') ? 1 : 0) - (has(a.properties, '_gift_unlock') ? 1 : 0));
      lines.forEach((l, i) => {
        const want = i === 0 ? 1 : 0;
        if ((parseInt(l.quantity, 10) || 0) !== want) updates[l.key] = want;
      });
    });

    /* strays: made free by the gift discount, named by no Gift block */
    cart.items.forEach((item) => {
      if (!giftIds.has(String(item.variant_id)) && freeByGiftDiscount(item, cfg.title)) updates[item.key] = 0;
    });

    return { updates, additions };
  }

  function changes(p) {
    return Object.keys(p.updates).length > 0 || p.additions.length > 0;
  }

  function sectionIds() {
    if (typeof window.sugarCartSectionIds === 'function') return window.sugarCartSectionIds();
    return ['sugar-cart-drawer', 'cart-icon-bubble'];
  }

  async function post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({ sections: sectionIds(), sections_url: window.location.pathname }, body)),
    });
    const data = await res.json();
    if (!res.ok || (data && data.status)) throw new Error((data && (data.description || data.message)) || 'Cart error');
    return data;
  }

  async function freshCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  async function announce(result) {
    const cartData = window.SugarCart ? await window.SugarCart.get({ fresh: true }) : await freshCart();
    lastCart = isCart(cartData) ? cartData : lastCart;
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-quantity-gifts', cartData, sections: result && result.sections });
    }
    document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
  }

  /* Never more than three settling rounds that changed the cart in ten seconds */
  function allowed() {
    const now = Date.now();
    while (recent.length && now - recent[0] > 10000) recent.shift();
    return recent.length < 3;
  }

  async function run(cart, force) {
    const cfg = config();
    if (!cfg) return;
    if (!isCart(cart)) cart = await freshCart();
    if (!isCart(cart)) return;
    lastCart = cart;
    const p = plan(cart, cfg);
    if (!changes(p)) return;
    if (!force && !allowed()) return;
    recent.push(Date.now());
    let result = null;
    if (Object.keys(p.updates).length) result = await post('/cart/update.js', { updates: p.updates });
    if (p.additions.length) result = await post('/cart/add.js', { items: p.additions });
    await announce(result);
  }

  function settle(cart, force) {
    if (busy) {
      dirty = true;
      return busy;
    }
    busy = run(cart, force)
      .catch((e) => console.error('[Cart Quantity Gifts]', e.message || e))
      .finally(() => {
        busy = null;
        if (dirty) {
          dirty = false;
          queue(null);
        }
      });
    return busy;
  }

  function queue(cart) {
    pending = cart || pending;
    clearTimeout(timer);
    timer = setTimeout(() => {
      const next = pending;
      pending = null;
      timer = null;
      settle(next);
    }, 250);
  }

  /* Out of step right now? The last cart we saw, else what the Liquid rendered */
  function outOfStep() {
    const cfg = config();
    if (!cfg) return false;
    if (busy || timer) return true;
    if (isCart(lastCart)) return changes(plan(lastCart, cfg));
    const state = cfg.root.querySelector('[data-cqg-state]');
    return !!state && state.dataset.needsSync === 'true';
  }

  let leaving = false;
  async function settleThenCheckout() {
    if (leaving) return;
    leaving = true;
    try {
      clearTimeout(timer);
      timer = null;
      if (busy) await busy;
      await settle(await freshCart(), true);
    } finally {
      window.location.href = '/checkout';
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest && e.target.closest(CHECKOUT);
    if (!btn || !outOfStep()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    settleThenCheckout();
  }, true);

  document.addEventListener('submit', (e) => {
    const submitter = e.submitter;
    if (!submitter || submitter.name !== 'checkout' || !outOfStep()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    settleThenCheckout();
  }, true);

  /* The nudge's add button: one more unit of the newest counted line */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-cqg-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    try {
      const qty = (parseInt(btn.dataset.qty, 10) || 0) + 1;
      const result = await post('/cart/change.js', { id: btn.dataset.key, quantity: qty });
      lastCart = isCart(result) ? result : lastCart;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-quantity-gifts', cartData: result, sections: result.sections });
      }
      document.dispatchEvent(new CustomEvent('cart:update', { detail: result }));
    } catch (err) {
      console.error('[Cart Quantity Gifts]', err.message || err);
      btn.disabled = false;
      btn.removeAttribute('aria-busy');
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === 'cart-quantity-gifts') return;
      queue(cartOf(event));
    });
  }
  document.addEventListener('cart:update', (e) => {
    const c = cartOf(e.detail);
    if (c) lastCart = c;
    queue(c);
  });

  /* A cart that arrived out of step (the Liquid says so) is settled once on load */
  function boot() {
    const state = document.querySelector(ROOT + ' [data-cqg-state]');
    if (state && state.dataset.needsSync === 'true') queue(null);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.CartQuantityGifts = { settle: () => settle(null, true) };
})();
